import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Building2, Zap, Eye, EyeOff, Check, X, Loader2, ArrowLeft, PartyPopper, Gift } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import DeploymentProgressModal from './DeploymentProgressModal';
import { TierSelectorCards } from './subscription';
import { SubscriptionTier } from '../services/subscriptionApi';
import { useAuthBranding, useSoftLaunch } from '../hooks';
import { Turnstile } from '@marsidev/react-turnstile';
import { usePasswordPolicy, evaluatePassword } from '../hooks/usePasswordPolicy';
import PasswordRequirements from './common/PasswordRequirements';

interface RegisterProps {
  onToggleMode: () => void;
}

const Register: React.FC<RegisterProps> = ({ onToggleMode }) => {
  // Step management
  const [checkingPassword, setCheckingPassword] = useState(false);
const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    companyName: '',
    workspaceName: '',
    workspaceSlug: '',
    region: 'US',    
    tosAccepted: false,
  });

  const { branding } = useAuthBranding('Create Account');
  const { isEnabled: isSoftLaunch, trialDays, messaging: softLaunchMessaging, standardTrialDays } = useSoftLaunch();
  const [workspaceNameManuallyEdited, setWorkspaceNameManuallyEdited] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [showSlugEditor, setShowSlugEditor] = useState(false);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deploymentModalOpen, setDeploymentModalOpen] = useState(false);
  const [workspaceId, setWorkspaceId] = useState<number | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string>('');

  // Slug validation state
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable'>('idle');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [step3Ready, setStep3Ready] = useState(false);

  // Access code state
const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [accessCode, setAccessCode] = useState('');
  const [accessCodeValidated, setAccessCodeValidated] = useState(false);
  const [accessCodeError, setAccessCodeError] = useState<string | null>(null);
  const [unlockedTier, setUnlockedTier] = useState<SubscriptionTier | null>(null);
  const [showAccessCodeInput, setShowAccessCodeInput] = useState(false);

const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // PPP parity: token carried from the marketing /trial/ link, plus a live
  // /api/parity check so the in-app plan cards match marketing prices.
  const [parityToken, setParityToken] = useState<string | null>(searchParams.get('t'));
  const [parityDiscountPct, setParityDiscountPct] = useState<number | null>(null);
  const [parityCountry, setParityCountry] = useState<string | null>(null);
  useEffect(() => {
    fetch('/api/parity')
      .then((r) => r.json())
      .then((d) => {
        if (d && d.eligible && d.discountPct) {
          setParityDiscountPct(d.discountPct);
          setParityCountry(d.country || null);
          if (d.token) setParityToken(d.token);
        }
      })
      .catch(() => {});
  }, []);

// Password validation state — rules come from the server policy, never
  // hardcoded. Breach screening and name/email screening run server-side
  // only, so passing these checks does not guarantee acceptance.
  const { policy: passwordPolicy } = usePasswordPolicy();
  const { meetsClientChecks: passwordValid } = evaluatePassword(
    formData.password,
    passwordPolicy
  );
  const passwordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;

  // Debounced slug check
  const checkSlugAvailability = useCallback(async (slug: string) => {
    if (!slug || slug.length < 4) {
      setSlugStatus('idle');
      setSlugError(slug.length > 0 && slug.length < 4 ? 'Slug must be at least 4 characters' : null);
      return;
    }

const slugRegex = /^[a-z]([a-z0-9-]*[a-z0-9])?$/;
    if (!slugRegex.test(slug) || slug.includes('--')) {
      setSlugStatus('unavailable');
      setSlugError('Must start with a letter; only lowercase letters, numbers, and hyphens allowed');
      return;
    }

    setSlugStatus('checking');
    setSlugError(null);

    try {
      const response = await fetch(`/api/auth/password/check-slug/${encodeURIComponent(slug)}`);
      const data = await response.json();

      if (data.available) {
        setSlugStatus('available');
        setSlugError(null);
      } else {
        setSlugStatus('unavailable');
        setSlugError(data.error || 'This slug is unavailable');
      }
    } catch (err) {
      console.error('Slug check failed:', err);
      setSlugStatus('idle');
      setSlugError(null);
    }
  }, []);

// Sync region from server-provided branding (single source of truth)
  useEffect(() => {
    if (branding?.region) {
      setFormData(prev => ({ ...prev, region: branding.region as string }));
    }
  }, [branding?.region]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.workspaceSlug) {
        checkSlugAvailability(formData.workspaceSlug);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.workspaceSlug, checkSlugAvailability]);

  // Check for access_code in URL on mount
  useEffect(() => {
    const codeFromUrl = searchParams.get('access_code');
    if (codeFromUrl) {
      setAccessCode(codeFromUrl);
      setShowAccessCodeInput(true);
      validateAccessCode(codeFromUrl);
    }
  }, [searchParams]);


  const validateAccessCode = async (code: string) => {
    if (!code.trim()) {
      setAccessCodeValidated(false);
      setAccessCodeError(null);
      setUnlockedTier(null);
      return;
    }

    try {
      const response = await fetch('/api/tier-access/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.toUpperCase() }),
      });
      const data = await response.json();

      if (data.valid && data.accessCode) {
        setAccessCodeValidated(true);
        setAccessCodeError(null);
        setUnlockedTier(data.accessCode.tier);
        setSelectedTier(data.accessCode.tier);
      } else {
        setAccessCodeValidated(false);
        setAccessCodeError(data.error || 'Invalid access code');
        setUnlockedTier(null);
      }
    } catch (err) {
      setAccessCodeError('Failed to validate code');
      setAccessCodeValidated(false);
    }
  };

  const handleAccessCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setAccessCode(value);
    setAccessCodeValidated(false);
    setAccessCodeError(null);
  };

  const handleAccessCodeApply = () => {
    if (accessCode.trim()) {
      validateAccessCode(accessCode);
    }
  };

  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'companyName') {
      const newState: any = { companyName: value };

      if (!workspaceNameManuallyEdited) {
        newState.workspaceName = value;
      }
      if (!slugManuallyEdited) {
        newState.workspaceSlug = generateSlug(value);
      }

      setFormData(prev => ({ ...prev, ...newState }));
    } else if (name === 'workspaceName') {
      setWorkspaceNameManuallyEdited(true);
      const newState: any = { workspaceName: value };
      if (!slugManuallyEdited) {
        newState.workspaceSlug = generateSlug(value);
      }
      setFormData(prev => ({ ...prev, ...newState }));
    } else if (name === 'workspaceSlug') {
      setSlugManuallyEdited(true);
      const normalizedSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .substring(0, 50);
      setFormData(prev => ({ ...prev, workspaceSlug: normalizedSlug }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const isStepValid = (step: number): boolean => {
if (step === 1) {
      return (
        formData.name.trim().length > 0 &&
        formData.email.trim().length > 0 &&
        formData.email.includes('@') &&
        passwordValid &&
        passwordsMatch &&
        formData.tosAccepted
      );
    }
    if (step === 2) {
      return (
        formData.companyName.trim().length > 0 &&
        formData.workspaceName.trim().length > 0 &&
        formData.workspaceSlug.length >= 4 &&
        slugStatus !== 'checking' &&
        slugStatus !== 'unavailable'
      );
    }
if (step === 3) {
      return step3Ready && (isSoftLaunch || !!selectedTier) && !!captchaToken;
    }
    return true;
  };

const handleNext = async () => {
    setError(null);
    if (!isStepValid(currentStep)) return;

    // Server-side policy check before leaving the password step. The browser
    // can only check length — breach screening and name/email screening run
    // server-side. Without this, a breached password isn't caught until final
    // submit, after the user has filled in two more screens.
    if (currentStep === 1) {
      setCheckingPassword(true);
      try {
        const res = await fetch('/api/auth/check-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            password: formData.password,
            email: formData.email,
            name: formData.name,
            workspaceName: formData.workspaceName,
            companyName: formData.companyName,
          }),
        });
        const data = await res.json();
        if (data && data.valid === false) {
          setError(data.errors?.[0] || 'Please choose a different password');
          setCheckingPassword(false);
          return;
        }
      } catch {
        // Fail open — a network blip must not block signup. The register
        // endpoint enforces the policy regardless.
      }
      setCheckingPassword(false);
    }

    if (currentStep === 2) {
      setStep3Ready(false);
      setTimeout(() => setStep3Ready(true), 300);
    }
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

if (!isSoftLaunch && !selectedTier) {
      setError('Please select a subscription plan');
      setLoading(false);
      return;
    }
    try {
      const response = await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        companyName: formData.companyName,
        workspaceName: formData.workspaceName,
        workspaceSlug: formData.workspaceSlug,
        region: formData.region,
	tierId: isSoftLaunch ? 1 : selectedTier?.id,
        tierCode: isSoftLaunch ? 'trial_growth' : selectedTier?.tier_code,
softLaunch: isSoftLaunch,
accessCode: accessCodeValidated ? accessCode : undefined,
        captchaToken: captchaToken || '',
        parityToken: parityToken || undefined,
      });

      const workspace = response?.workspace;

      if (workspace) {
        setWorkspaceId(workspace.id);
        setWorkspaceName(workspace.name);
        setDeploymentModalOpen(true);
        setLoading(false);
      } else {
        navigate('/dashboard');
      }
} catch (err: any) {
      setError(err.message || 'Registration failed');
      setLoading(false);
      setCaptchaToken(null);
    }
  };

  const handleDeploymentComplete = () => {
    setDeploymentModalOpen(false);
    navigate('/dashboard');
  };

  // Styles
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9375rem',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box',
    fontFamily: 'var(--font-sans)',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--v2-text)',
    marginBottom: '6px',
  };

  const helperStyle: React.CSSProperties = {
    fontSize: '0.8125rem',
    color: 'var(--v2-text-tertiary)',
    marginTop: '6px',
  };


  const stepIconContainerStyle = (bgColor: string): React.CSSProperties => ({
    width: '40px',
    height: '40px',
    borderRadius: 'var(--radius-md)',
    background: bgColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      
      {deploymentModalOpen && workspaceId && (
        <DeploymentProgressModal
          open={deploymentModalOpen}
          workspaceId={workspaceId}
          workspaceName={workspaceName}
          onComplete={handleDeploymentComplete}
        />
      )}
      {!deploymentModalOpen && (
        <div style={{
          minHeight: '100vh',
          backgroundColor: 'var(--v2-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          fontFamily: 'var(--font-sans)',
        }}>
          <div style={{
            backgroundColor: 'var(--v2-surface)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-xl)',
            border: '1px solid var(--v2-border)',
            maxWidth: currentStep === 3 ? '900px' : '560px',
            transition: 'max-width 0.3s ease',
            width: '100%',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '32px 32px 24px',
              borderBottom: '1px solid var(--v2-border)',
              textAlign: 'center',
            }}>
              <h1 style={{
                fontSize: '1.75rem',
                fontWeight: 700,
                color: 'var(--v2-text)',
                margin: '0 0 8px 0',
              }}>
                Create Your Account
              </h1>
              <p style={{
                fontSize: '0.9375rem',
                color: 'var(--v2-text-tertiary)',
                margin: '0 0 24px 0',
              }}>
                Get started with {branding?.company_name || 'DashboardFox'} in minutes
              </p>

              {/* Progress */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {[1, 2, 3].map((step) => (
                  <React.Fragment key={step}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: currentStep >= step ? 'var(--v2-primary)' : 'var(--v2-border)',
                      color: currentStep >= step ? '#ffffff' : 'var(--v2-text-tertiary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      transition: 'all 0.2s',
                    }}>
                      {currentStep > step ? <Check size={16} strokeWidth={2.5} /> : step}
                    </div>
                    {step < 3 && (
                      <div style={{
                        width: '48px',
                        height: '2px',
                        backgroundColor: currentStep > step ? 'var(--v2-primary)' : 'var(--v2-border)',
                        transition: 'background-color 0.2s',
                      }} />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '8px',
                padding: '0 8px',
              }}>
                <span style={{ fontSize: '0.75rem', color: currentStep >= 1 ? 'var(--v2-primary)' : 'var(--v2-text-tertiary)', fontWeight: 500 }}>Account</span>
                <span style={{ fontSize: '0.75rem', color: currentStep >= 2 ? 'var(--v2-primary)' : 'var(--v2-text-tertiary)', fontWeight: 500 }}>Workspace</span>
                <span style={{ fontSize: '0.75rem', color: currentStep >= 3 ? 'var(--v2-primary)' : 'var(--v2-text-tertiary)', fontWeight: 500 }}>Plan</span>
              </div>
            </div>

            {/* Content */}
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '24px 32px' }}>
                {error && (
                  <div style={{
                    backgroundColor: 'var(--v2-error-bg)',
                    border: '1px solid #fecaca',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px 16px',
                    marginBottom: '20px',
                  }}>
                    <p style={{ fontSize: '0.875rem', color: 'var(--v2-error)', margin: 0 }}>{error}</p>
                  </div>
                )}

                {/* STEP 1: Account Information */}
                {currentStep === 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={stepIconContainerStyle('linear-gradient(135deg, var(--v2-primary) 0%, #818cf8 100%)')}>
                        <User size={20} strokeWidth={1.5} color="#ffffff" />
                      </div>
                      <div>
                        <h2 style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'var(--v2-text)', margin: 0 }}>
                          Your Account
                        </h2>
                        <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
                          Create your login credentials
                        </p>
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        style={inputStyle}
                        autoFocus
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Password *</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Create a strong password"
                          style={{ ...inputStyle, paddingRight: '44px' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--v2-text-tertiary)',
                            padding: '4px',
                            display: 'flex',
                          }}
                        >
                          {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
                        </button>
                      </div>

{/* Real-time password validation */}
                      <PasswordRequirements
                        password={formData.password}
                        policy={passwordPolicy}
                        columns={1}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Confirm Password *</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm your password"
                          style={{
                            ...inputStyle,
                            paddingRight: '44px',
                            borderColor: formData.confirmPassword.length > 0
                              ? (passwordsMatch ? 'var(--v2-success)' : 'var(--v2-error)')
                              : 'var(--v2-border)',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--v2-text-tertiary)',
                            padding: '4px',
                            display: 'flex',
                          }}
                        >
                          {showConfirmPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
                        </button>
                      </div>
                      {formData.confirmPassword.length > 0 && (
                        <p style={{
                          ...helperStyle,
                          color: passwordsMatch ? 'var(--v2-success)' : 'var(--v2-error)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}>
                          {passwordsMatch ? <><Check size={14} strokeWidth={2} /> Passwords match</> : 'Passwords do not match'}
                        </p>
                      )}
</div>

                    {/* ToS Consent */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '14px 16px',
                      backgroundColor: 'var(--v2-surface-alt)',
                      border: `1px solid ${formData.tosAccepted ? 'var(--v2-success)' : 'var(--v2-border)'}`,
                      borderRadius: 'var(--radius-md)',
                      transition: 'border-color 0.15s',
                    }}>
                      <input
                        type="checkbox"
                        id="tosAccepted"
                        checked={formData.tosAccepted}
                        onChange={(e) => setFormData(prev => ({ ...prev, tosAccepted: e.target.checked }))}
                        style={{ width: '18px', height: '18px', accentColor: 'var(--v2-primary)', marginTop: '2px', flexShrink: 0, cursor: 'pointer' }}
                      />
                      <label htmlFor="tosAccepted" style={{ fontSize: '0.8125rem', color: 'var(--v2-text)', cursor: 'pointer', lineHeight: 1.5 }}>
                        I agree to the{' '}
                        <a href="https://www.dashboardfox.com/legal/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--v2-primary)', textDecoration: 'underline' }}>
                          Terms of Service
                        </a>
                        {' '}and{' '}
                        <a href="https://www.dashboardfox.com/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--v2-primary)', textDecoration: 'underline' }}>
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                  </div>
                )}

                {/* STEP 2: Workspace Information */}
                {currentStep === 2 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={stepIconContainerStyle('linear-gradient(135deg, #dc2626 0%, #f87171 100%)')}>
                        <Building2 size={20} strokeWidth={1.5} color="#ffffff" />
                      </div>
                      <div>
                        <h2 style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'var(--v2-text)', margin: 0 }}>
                          Your Workspace
                        </h2>
                        <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
                          Set up your company workspace
                        </p>
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>Company Name *</label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="Acme Corporation"
                        style={inputStyle}
                        autoFocus
                      />
                      <p style={helperStyle}>Used for billing and invoices</p>
                    </div>

                    <div>
                      <label style={labelStyle}>Workspace Name *</label>
                      <input
                        type="text"
                        name="workspaceName"
                        value={formData.workspaceName}
                        onChange={handleChange}
                        placeholder="Acme Analytics"
                        style={inputStyle}
                      />
                      <p style={helperStyle}>Display name (can differ from company name)</p>
                    </div>

                    {/* Inline Slug Preview */}
                    <div style={{
                      backgroundColor: 'var(--v2-surface-alt)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '14px 16px',
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: showSlugEditor ? '12px' : 0,
                      }}>
                        <div>
                          <p style={{
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            color: 'var(--v2-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            margin: '0 0 4px 0',
                          }}>
                            Your Workspace URL
                          </p>
                          <p style={{
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            color: slugStatus === 'unavailable' ? 'var(--v2-error)' : slugStatus === 'available' ? 'var(--v2-success)' : 'var(--v2-text)',
                            margin: 0,
                            fontFamily: 'var(--font-mono)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}>
                            {formData.workspaceSlug || 'your-slug'}.dashboardfox.app
                            {slugStatus === 'checking' && <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
                            {slugStatus === 'available' && <Check size={14} strokeWidth={2} color="var(--v2-success)" />}
                            {slugStatus === 'unavailable' && <X size={14} strokeWidth={2} color="var(--v2-error)" />}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowSlugEditor(!showSlugEditor)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--v2-primary)',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            padding: '4px 8px',
                          }}
>
                          {showSlugEditor ? 'Done' : 'Edit'}
                        </button>
                      </div>

                      {!showSlugEditor && (
                        formData.workspaceSlug.length > 0 && formData.workspaceSlug.length < 4 ? (
                          <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: '8px 0 0 0' }}>
                            Minimum 4 characters
                          </p>
                        ) : slugError ? (
                          <p style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', margin: '8px 0 0 0' }}>
                            {slugError}
                          </p>
                        ) : null
                      )}

                      {showSlugEditor && (
                        <div>
                          <input
                            type="text"
                            name="workspaceSlug"
                            value={formData.workspaceSlug}
                            onChange={handleChange}
                            placeholder="acme-corp"
                            style={{
                              ...inputStyle,
                              fontFamily: 'var(--font-mono)',
                              backgroundColor: 'var(--v2-surface)',
                              borderColor: slugStatus === 'unavailable' ? 'var(--v2-error)' : slugStatus === 'available' ? 'var(--v2-success)' : 'var(--v2-border)',
                            }}
                          />
                          {slugError && (
                            <p style={{ ...helperStyle, color: 'var(--v2-error)', marginTop: '6px' }}>
                              {slugError}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

{/* Region Info (read-only, set by deployment) */}
                    <div>
                      <label style={labelStyle}>Server Region</label>
                      <div style={{
                        backgroundColor: 'var(--v2-surface-alt)',
                        border: '1px solid var(--v2-border)',
                        borderRadius: 'var(--radius-md)',
                        padding: '14px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}>
                        <span style={{ fontSize: '1.25rem' }}>
                          {formData.region === 'EU' ? '🇪🇺' : '🇺🇸'}
                        </span>
                        <div>
                          <p style={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: 'var(--v2-text)',
                            margin: 0,
                          }}>
                            {formData.region === 'EU' ? 'European Union' : 'United States'} Region
                          </p>
                          <p style={{
                            fontSize: '0.75rem',
                            color: 'var(--v2-text-tertiary)',
                            margin: '2px 0 0 0',
                          }}>
                            {formData.region === 'EU' 
                              ? 'Your data will be stored in EU data centers (GDPR compliant)'
                              : 'Your data will be stored in US data centers'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3: Plan Selection */}
                {currentStep === 3 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
		    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <div style={stepIconContainerStyle('linear-gradient(135deg, #d97706 0%, #fbbf24 100%)')}>
                        {isSoftLaunch ? <Gift size={20} strokeWidth={1.5} color="#ffffff" /> : <Zap size={20} strokeWidth={1.5} color="#ffffff" />}
                      </div>
                      <div>
                        <h2 style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'var(--v2-text)', margin: 0 }}>
                          {isSoftLaunch ? softLaunchMessaging.title : 'Choose Your Plan'}
                        </h2>
                        <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
                          {isSoftLaunch ? softLaunchMessaging.subtitle : `Start with a ${standardTrialDays}-day free trial`}
                        </p>
                      </div>
                    </div>

		{/* Soft Launch Banner */}
                    {isSoftLaunch && (
                      <div style={{
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        border: '1px solid #fcd34d',
                        borderRadius: 'var(--radius-md)',
                        padding: '16px 20px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <Gift size={20} strokeWidth={1.5} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                          <div>
                            <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#92400e', margin: '0 0 4px 0' }}>
                              {softLaunchMessaging.badge} — {trialDays} Days Free
                            </p>
                            <p style={{ fontSize: '0.8125rem', color: '#a16207', margin: 0, lineHeight: 1.5 }}>
                              {softLaunchMessaging.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Access Code - Collapsed (hidden during soft launch) */}
                    {!isSoftLaunch && <div>
                      {!showAccessCodeInput ? (
                        <button
                          type="button"
                          onClick={() => setShowAccessCodeInput(true)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--v2-primary)',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          Have an access code?
                        </button>
                      ) : (
                        <div style={{
                          backgroundColor: 'var(--v2-surface-alt)',
                          border: '1px solid var(--v2-border)',
                          borderRadius: 'var(--radius-md)',
                          padding: '14px 16px',
                        }}>
                          <label style={{
                            display: 'block',
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            color: 'var(--v2-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: '8px',
                          }}>
                            Access Code
                          </label>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <input
                                type="text"
                                value={accessCode}
                                onChange={handleAccessCodeChange}
                                placeholder="Enter code"
                                style={{
                                  ...inputStyle,
                                  backgroundColor: 'var(--v2-surface)',
                                  borderColor: accessCodeError ? 'var(--v2-error)' : accessCodeValidated ? 'var(--v2-success)' : 'var(--v2-border)',
                                }}
                              />
                              {(accessCodeError || accessCodeValidated) && (
                                <p style={{
                                  ...helperStyle,
                                  color: accessCodeError ? 'var(--v2-error)' : 'var(--v2-success)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}>
                                  {accessCodeError || <><Check size={14} strokeWidth={2} /> Unlocked: {unlockedTier?.tier_name}</>}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={handleAccessCodeApply}
                              disabled={!accessCode.trim() || accessCodeValidated}
                              style={{
                                padding: '12px 16px',
                                backgroundColor: (!accessCode.trim() || accessCodeValidated) ? 'var(--v2-border)' : 'var(--v2-surface)',
                                color: (!accessCode.trim() || accessCodeValidated) ? 'var(--v2-text-muted)' : 'var(--v2-text)',
                                border: '1px solid var(--v2-border)',
                                borderRadius: 'var(--radius-md)',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: (!accessCode.trim() || accessCodeValidated) ? 'not-allowed' : 'pointer',
                              }}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      )}
		    </div>}
                    {/* Unlocked Notice */}

                    {unlockedTier && (
                      <div style={{
                        backgroundColor: 'var(--v2-success-bg)',
                        border: '1px solid #86efac',
                        borderRadius: 'var(--radius-md)',
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}>
                        <PartyPopper size={18} strokeWidth={1.5} color="var(--v2-success)" />
                        <p style={{ fontSize: '0.875rem', color: 'var(--v2-success-dark)', margin: 0 }}>
                          Special plan unlocked: <strong>{unlockedTier.tier_name}</strong>
                        </p>
                      </div>
                    )}

{/* Tier Selection */}
                    <TierSelectorCards
                      selectedTierId={selectedTier?.id}
                      onTierSelect={setSelectedTier}
                      filterCodes={['starter', 'growth', 'scale']}
                      unlockedTier={unlockedTier}
                      softLaunchMode={isSoftLaunch}
                      softLaunchDays={trialDays}
                      parityDiscountPct={parityDiscountPct}
                      parityCountry={parityCountry}
                    />

<p style={{
                      fontSize: '0.8125rem',
                      color: 'var(--v2-text-tertiary)',
                      textAlign: 'center',
                      margin: '8px 0 0 0',
                    }}>
                      {isSoftLaunch 
                        ? 'No credit card required. Full access during Early Access period.'
                        : 'No credit card required. Cancel anytime.'}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                      <Turnstile
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || ''}
                        onSuccess={(token) => setCaptchaToken(token)}
                        onExpire={() => setCaptchaToken(null)}
                        onError={() => setCaptchaToken(null)}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '16px 32px 32px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      style={{
                        flex: 1,
                        padding: '14px 24px',
                        backgroundColor: 'var(--v2-surface)',
                        color: 'var(--v2-text)',
                        border: '1px solid var(--v2-border)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      <ArrowLeft size={16} strokeWidth={1.5} /> Back
                    </button>
                  )}

                  {currentStep < totalSteps ? (
                    <button
                      type="button"
onClick={handleNext}
disabled={!isStepValid(currentStep) || checkingPassword}
                      style={{
                        flex: currentStep === 1 ? 1 : 2,
                        padding: '14px 24px',
                        backgroundColor: !isStepValid(currentStep) ? 'var(--v2-border)' : 'var(--v2-primary)',
                        color: !isStepValid(currentStep) ? 'var(--v2-text-muted)' : '#ffffff',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        cursor: !isStepValid(currentStep) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading || !isStepValid(3)}
                      style={{
                        flex: 2,
                        padding: '14px 24px',
                        backgroundColor: (loading || !isStepValid(3)) ? 'var(--v2-border)' : 'var(--v2-primary)',
                        color: (loading || !isStepValid(3)) ? 'var(--v2-text-muted)' : '#ffffff',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        cursor: (loading || !isStepValid(3)) ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      {loading && <Loader2 size={18} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
                      {loading ? 'Creating Account...' : 'Create Account & Start Trial'}
                    </button>
                  )}
                </div>

                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--v2-text-tertiary)',
                  textAlign: 'center',
                  margin: 0,
                }}>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={onToggleMode}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--v2-primary)',
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '0.875rem',
                    }}
                  >
                    Sign in here
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Register;
